// NotifyMeForm — self-contained "notify me when this launches" email capture
// for one coming-soon lead magnet card on /library. Mirrors the state-machine
// shape of MarketingOptIn.tsx: idle -> saving -> done | error, posts directly
// to its own endpoint, no parent form dependency.

import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

interface Props {
  slug: string;
}

export function NotifyMeForm({ slug }: Props) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Enter a valid email.');
      return;
    }
    setStatus('saving');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/public/magnets/${encodeURIComponent(slug)}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setStatus('error');
        setError('Could not save that. Please try again.');
        return;
      }
      setStatus('done');
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div style={doneBox} data-notify-form="done">
        You're on the list ✓
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} style={formRow} data-notify-form="form">
      <input
        type="email"
        placeholder="you@practice.com"
        value={email}
        disabled={status === 'saving'}
        onChange={(e) => setEmail(e.target.value)}
        style={inputStyle}
      />
      <button type="submit" disabled={status === 'saving'} style={buttonStyle}>
        {status === 'saving' ? 'Saving…' : 'Notify me'}
      </button>
      {error && <div style={errorStyle}>{error}</div>}
    </form>
  );
}

const formRow: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 };
const inputStyle: CSSProperties = {
  padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(10,10,46,0.18)',
  fontSize: 13, fontFamily: 'inherit',
};
const buttonStyle: CSSProperties = {
  padding: '9px 14px', borderRadius: 8, border: 'none', background: '#4F46FF',
  color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
const doneBox: CSSProperties = {
  marginTop: 10, fontSize: 13, fontWeight: 600, color: '#1a7f37',
};
const errorStyle: CSSProperties = { fontSize: 12, color: '#c0392b' };
